"use client"

import type React from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Upload, RefreshCw } from "lucide-react"
import { useRef } from "react"

interface FileUploadProps {
    selectedFile: File | null
    onFileSelect: (file: File | null) => void
    onUpload: () => void
    isLoading: boolean
}

export const FileUpload = ({ selectedFile, onFileSelect, onUpload, isLoading }: FileUploadProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        onFileSelect(file || null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        const file = e.dataTransfer.files[0]
        if (file) {
            onFileSelect(file)
        }
    }

    return (
        <Card className="w-full">
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-[#613F4F]" />
                    <CardTitle className="text-lg text-[#613F4F]">Upload de Arquivo</CardTitle>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Drop de arquivo */}
                <div
                    className={`
                        border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer
                        ${selectedFile
                            ? "border-[#F6D040] bg-[#F6D040]/5"
                            : "border-gray-300 hover:border-[#F6D040] hover:bg-[#F6D040]/5"
                        }
                        ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => !isLoading && fileInputRef.current?.click()}
                >
                    <div className="flex flex-col items-center gap-4">
                        <div
                            className={`
                            rounded-full p-4 transition-colors
                            ${selectedFile ? "bg-[#F6D040]/20" : "bg-gray-100"}
                        `}
                        >
                            <Upload className={`h-8 w-8 ${selectedFile ? "text-[#F6D040]" : "text-gray-400"}`} />
                        </div>

                        <div className="space-y-2">
                            {selectedFile ? (
                                <div className="space-y-1">
                                    <p className="font-medium text-[#613F4F]">Arquivo selecionado:</p>
                                    <p className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border">{selectedFile.name}</p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    <p className="font-medium text-gray-700">Arraste e solte seu arquivo aqui</p>
                                    <p className="text-sm text-gray-500">ou clique para procurar</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                    />
                </div>

                <div className="text-center">
                    <p className="text-xs text-gray-500">
                        Formatos suportados: <span className="font-medium">.xlsx, .xls, .csv</span> • Tamanho máximo:{" "}
                        <span className="font-medium">10MB</span>
                    </p>
                </div>

                <div className="flex gap-3 justify-center">
                    {!selectedFile ? (
                        <Button
                            className="bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90 px-6"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isLoading}
                        >
                            <Upload className="mr-2 h-4 w-4" />
                            Procurar Arquivos
                        </Button>
                    ) : (
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = ""; 
                                        fileInputRef.current.click();
                                    }
                                }}
                                disabled={isLoading}
                            >
                                <Upload className="mr-2 h-4 w-4" />
                                Trocar Arquivo
                            </Button>
                            <Button
                                className="bg-[#613F4F] text-white hover:bg-[#613F4F]/90 px-6"
                                onClick={onUpload}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                        Processando...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="mr-2 h-4 w-4" />
                                        Fazer Análise
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
